/**
 * useSidebarProjectsAndThreads — pure derivation of the project + thread data
 * the sidebar renders.
 *
 * Owns:
 *  - `orderedProjects`, `physicalToLogicalKey`
 *  - `sidebarProjects` (logical merge of local + remote environments)
 *  - `sidebarProjectByKey`, `sidebarThreadByKey`
 *  - `threadsByProjectKey`, `activeRouteProjectKey`
 *  - `sortedProjects` (respecting manual/updatedAt/createdAt sort)
 *  - `visibleSidebarThreadKeys` (⌘1..⌘9 candidate set)
 *  - `threadJumpCommandByKey`, `threadJumpThreadKeys`
 *  - prewarm of detail subscriptions for visible threads
 *
 * Extracted from `useSidebarOrchestration` so the orchestration hook stays
 * under the 400-line cap.  Behaviour is a pure move — no memo identity
 * changes, no dependency changes.
 */

import { useEffect, useMemo } from "react";
import {
  parseScopedThreadKey,
  scopedProjectKey,
  scopedThreadKey,
  scopeProjectRef,
  scopeThreadRef,
} from "@cmmd-forge/client-runtime";
import type { EnvironmentId, ProjectId } from "@cmmd-forge/contracts";
import type {
  SidebarProjectSortOrder,
  SidebarThreadSortOrder,
} from "@cmmd-forge/contracts/settings";
import { retainThreadDetailSubscription } from "../../environments/runtime/service";
import { threadJumpCommandForIndex } from "../../keybindings";
import { sortThreads } from "../../lib/threadSort";
import {
  getSidebarThreadIdsToPrewarm,
  orderItemsByPreferredIds,
  sortProjectsForSidebar,
} from "../Sidebar.logic";
import type { Project, SidebarThreadSummary } from "../../types";
import { aggregateProjectAvailability } from "./aggregateProjectAvailability";
import { useSidebarProjectGrouping } from "./useSidebarProjectGrouping";
import {
  type SidebarProjectSnapshot,
  type SidebarProjectSourceMetadata,
  type SidebarProjectWorkspaceIdentity,
  type SidebarProjectWorktreeMember,
} from "./SidebarProjectItem";
import { resolveRuntimeActionRoute } from "../../environments/runtimeActionRouting";
import {
  readRuntimeSurfaceState,
  type RuntimeSurfaceState,
} from "../../environments/runtimeSurface";
import {
  deriveWorktreeLabelsForGroup,
  formatWorktreeLabel,
  type WorktreeLabel,
} from "./worktreeLabel";
import {
  resolveSidebarEnvironmentRole,
  type SidebarEnvironmentRole,
} from "./sidebarEnvironmentRole";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const THREAD_PREVIEW_LIMIT = 6;

// ---------------------------------------------------------------------------
// Input / return types
// ---------------------------------------------------------------------------

type SavedEnvironmentRuntimeById = Readonly<
  Record<string, { readonly descriptor?: { readonly label?: string } | null } | undefined>
>;
type SavedEnvironmentRegistryById = Readonly<
  Record<string, { readonly label?: string } | undefined>
>;

export interface SidebarProjectsAndThreadsInput {
  readonly projects: readonly Project[];
  readonly sidebarThreads: readonly SidebarThreadSummary[];
  readonly projectOrder: readonly string[];
  readonly projectExpandedById: Readonly<Record<string, boolean>>;
  readonly expandedThreadListsByProject: ReadonlySet<string>;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly savedEnvironmentRegistry: SavedEnvironmentRegistryById;
  readonly savedEnvironmentRuntimeById: SavedEnvironmentRuntimeById;
  readonly workspaceIdentityByPhysicalProjectKey?: ReadonlyMap<
    string,
    SidebarProjectWorkspaceIdentity
  >;
  readonly sidebarProjectSortOrder: SidebarProjectSortOrder;
  readonly sidebarThreadSortOrder: SidebarThreadSortOrder;
  readonly routeThreadKey: string | null;
}

export interface SidebarProjectsAndThreadsResult {
  readonly physicalToLogicalKey: ReadonlyMap<string, string>;
  readonly sidebarProjects: readonly SidebarProjectSnapshot[];
  readonly sidebarProjectByKey: ReadonlyMap<string, SidebarProjectSnapshot>;
  readonly sidebarThreadByKey: ReadonlyMap<string, SidebarThreadSummary>;
  readonly threadsByProjectKey: ReadonlyMap<string, SidebarThreadSummary[]>;
  readonly activeRouteProjectKey: string | null;
  readonly activeRouteProjectRef: {
    readonly environmentId: EnvironmentId;
    readonly projectId: ProjectId;
  } | null;
  readonly sortedProjects: readonly SidebarProjectSnapshot[];
  readonly isManualProjectSorting: boolean;
  readonly visibleSidebarThreadKeys: readonly string[];
  readonly threadJumpCommandByKey: ReadonlyMap<
    string,
    NonNullable<ReturnType<typeof threadJumpCommandForIndex>>
  >;
  readonly threadJumpThreadKeys: readonly string[];
}

const isAvailable = (p: Project) => p.availability === undefined || p.availability === "available";

function deriveProjectSourceMetadata(input: {
  readonly runtime: RuntimeSurfaceState;
  readonly environmentPresence: SidebarProjectSnapshot["environmentPresence"];
  readonly targetEnvironmentRole: SidebarEnvironmentRole;
  readonly workspaceIdentity: SidebarProjectWorkspaceIdentity | null;
}): SidebarProjectSourceMetadata {
  const workSource = input.targetEnvironmentRole === "local" ? "desktop-local" : "cloud-vm";
  const executionRuntime =
    input.targetEnvironmentRole === "local" ? input.runtime.executionRuntime : "cloud";
  const linkState: SidebarProjectSourceMetadata["linkState"] =
    workSource === "cloud-vm"
      ? "cloud-only"
      : input.environmentPresence === "remote-only"
        ? "cloud-only"
        : input.environmentPresence === "mixed"
          ? "linked"
          : input.runtime.surface === "cloud-official"
            ? "local-only"
            : "unlinked";
  const hideLocalPath =
    workSource === "cloud-vm" ||
    input.runtime.surface === "cloud-official" ||
    input.runtime.surface === "desktop-cloud";
  const linkedWorkspaceDescription = input.workspaceIdentity
    ? `This project is linked to ${input.workspaceIdentity.name} CMMD context.`
    : "This local project is linked to Command/CMMD context.";
  const cloudWorkspaceDescription = input.workspaceIdentity
    ? `This project exists in ${input.workspaceIdentity.name} cloud context.`
    : "This project exists in a cloud or remote Forge environment.";
  const label = (() => {
    if (input.workspaceIdentity && linkState === "linked") {
      return `Linked to ${input.workspaceIdentity.name}`;
    }
    if (input.workspaceIdentity && linkState === "cloud-only") {
      return `${input.workspaceIdentity.name} cloud project`;
    }
    if (linkState === "cloud-only") return "Cloud project";
    if (linkState === "linked") return "Linked project";
    if (linkState === "unlinked") return "Unlinked local project";
    return "Local project";
  })();
  const description = (() => {
    if (workSource === "cloud-vm") {
      return input.workspaceIdentity
        ? `This project runs in your Forge Cloud VM and is linked to ${input.workspaceIdentity.name} CMMD context.`
        : "This project runs in your Forge Cloud VM.";
    }
    if (linkState === "cloud-only") return cloudWorkspaceDescription;
    if (linkState === "linked") return linkedWorkspaceDescription;
    if (linkState === "unlinked") {
      return "This desktop project is local and can be linked to Command/CMMD context.";
    }
    return "This project is local-only. Switch to Desktop Local before running filesystem, terminal, git, or project-write actions.";
  })();
  const actionRoute = resolveRuntimeActionRoute({
    actionKind: "project-write",
    surface: input.runtime.surface,
    workSource,
    executionRuntime,
  });

  return {
    actionAuthority: "runtime",
    surface: input.runtime.surface,
    hostKind: input.runtime.hostKind,
    workSource,
    executionRuntime,
    modelRuntime: input.runtime.modelRuntime,
    linkState,
    workspaceIdentity: input.workspaceIdentity,
    hideLocalPath,
    label,
    description,
    actionRouteLabel: actionRoute.title,
    actionRouteDescription: actionRoute.description,
    actionRouteAvailable: actionRoute.available,
    requiresPermissionGate: actionRoute.requiresPermissionGate,
  };
}

function hasRole(
  rolesByEnvironmentId: ReadonlyMap<EnvironmentId, SidebarEnvironmentRole>,
  members: readonly Project[],
  role: SidebarEnvironmentRole,
): boolean {
  return members.some((project) => rolesByEnvironmentId.get(project.environmentId) === role);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSidebarProjectsAndThreads(
  input: SidebarProjectsAndThreadsInput,
): SidebarProjectsAndThreadsResult {
  const {
    projects,
    sidebarThreads,
    projectOrder,
    projectExpandedById,
    expandedThreadListsByProject,
    primaryEnvironmentId,
    savedEnvironmentRegistry,
    savedEnvironmentRuntimeById,
    workspaceIdentityByPhysicalProjectKey,
    sidebarProjectSortOrder,
    sidebarThreadSortOrder,
    routeThreadKey,
  } = input;

  const orderedProjects = useMemo(
    () =>
      orderItemsByPreferredIds({
        items: projects,
        preferredIds: projectOrder,
        getId: (project) => scopedProjectKey(scopeProjectRef(project.environmentId, project.id)),
      }),
    [projectOrder, projects],
  );

  const { physicalToLogicalKey, resolveLogicalKey } = useSidebarProjectGrouping(orderedProjects);

  // The routed thread's physical project key (env+id), if any. We use this
  // to mark which member of a multi-worktree logical group is the "active"
  // checkout — the one whose threads + actions get the default focus.
  const routedPhysicalProjectKey = useMemo<string | null>(() => {
    if (!routeThreadKey) return null;
    const thread = sidebarThreads.find(
      (candidate) =>
        scopedThreadKey(scopeThreadRef(candidate.environmentId, candidate.id)) === routeThreadKey,
    );
    if (!thread) return null;
    return scopedProjectKey(scopeProjectRef(thread.environmentId, thread.projectId));
  }, [routeThreadKey, sidebarThreads]);

  const sidebarProjects = useMemo<SidebarProjectSnapshot[]>(() => {
    // readRuntimeSurfaceState() is intentionally NOT a memo dependency. Its
    // inputs are either boot-stable (isElectron, location.origin, desktop
    // bootstrap) or the primary-environment source, which changes in lockstep
    // with `primaryEnvironmentId` (already a dep below) — so this memo already
    // recomputes whenever the surface can meaningfully change. The only
    // non-correlated input is the dev-only localStorage view override, whose
    // staleness self-heals on the next primaryEnvironmentId change; making it
    // fully reactive would need a useSyncExternalStore subscription in
    // runtimeSurface.ts, which is unnecessary for production correctness.
    const runtime = readRuntimeSurfaceState();
    const rolesByEnvironmentId = new Map<EnvironmentId, SidebarEnvironmentRole>();
    for (const project of orderedProjects) {
      if (rolesByEnvironmentId.has(project.environmentId)) continue;
      rolesByEnvironmentId.set(
        project.environmentId,
        resolveSidebarEnvironmentRole({
          environmentId: project.environmentId,
          primaryEnvironmentId,
          runtimeSurface: runtime.surface,
          savedEnvironmentRegistry,
        }),
      );
    }

    const groupedMembers = new Map<string, Project[]>();
    for (const project of orderedProjects) {
      const logicalKey = resolveLogicalKey(project);
      const existing = groupedMembers.get(logicalKey);
      if (existing) {
        existing.push(project);
      } else {
        groupedMembers.set(logicalKey, [project]);
      }
    }

    const result: SidebarProjectSnapshot[] = [];
    const seen = new Set<string>();
    for (const project of orderedProjects) {
      const logicalKey = resolveLogicalKey(project);
      if (seen.has(logicalKey)) continue;
      seen.add(logicalKey);

      const members = groupedMembers.get(logicalKey)!;
      // Prefer a member that is BOTH (a) in the primary environment AND
      // (b) currently exists on disk. This keeps the row's name / identity
      // / scripts sourced from a healthy checkout when one of the members
      // is a stale (missing-on-disk) worktree merged in by path-shape
      // fallback.
      const representative: Project | undefined =
        (primaryEnvironmentId
          ? (members.find((p) => p.environmentId === primaryEnvironmentId && isAvailable(p)) ??
            members.find((p) => p.environmentId === primaryEnvironmentId))
          : undefined) ??
        members.find(isAvailable) ??
        members[0];
      if (!representative) continue;
      const hasLocal = hasRole(rolesByEnvironmentId, members, "local");
      const hasCloud = hasRole(rolesByEnvironmentId, members, "cloud");
      const hasRemote = hasRole(rolesByEnvironmentId, members, "remote");
      const hasCloudOrRemote = hasCloud || hasRemote;
      const environmentPresence =
        hasLocal && hasCloudOrRemote ? "mixed" : hasCloudOrRemote ? "remote-only" : "local-only";

      const refs = members.map((p) => scopeProjectRef(p.environmentId, p.id));
      const workspaceIdentity =
        members
          .map((member) =>
            workspaceIdentityByPhysicalProjectKey?.get(
              scopedProjectKey(scopeProjectRef(member.environmentId, member.id)),
            ),
          )
          .find((identity): identity is SidebarProjectWorkspaceIdentity => !!identity) ?? null;
      const remoteLabels = members
        .filter((p) => primaryEnvironmentId !== null && p.environmentId !== primaryEnvironmentId)
        .map((p) => {
          const rt = savedEnvironmentRuntimeById[p.environmentId];
          const saved = savedEnvironmentRegistry[p.environmentId];
          return rt?.descriptor?.label ?? saved?.label ?? p.environmentId;
        });

      // Build per-worktree members. Even for size-1 groups we still
      // produce a 1-element array so downstream renderers don't have to
      // special-case "no worktree info" — the array is just a richer view
      // of `memberProjectRefs`.
      const repositoryNameHint = (() => {
        const id = representative.repositoryIdentity;
        if (!id) return null;
        const candidates = new Set<string>();
        if (typeof id.name === "string" && id.name.length > 0) candidates.add(id.name);
        if (typeof id.owner === "string" && id.owner.length > 0) candidates.add(id.owner);
        return candidates.size > 0 ? candidates : null;
      })();
      const labels: readonly WorktreeLabel[] = deriveWorktreeLabelsForGroup(
        members.map((member) => ({
          cwd: member.cwd,
          repositorySubpath: member.repositoryIdentity?.repositorySubpath ?? null,
        })),
        repositoryNameHint,
      );
      const worktreeMembers: SidebarProjectWorktreeMember[] = members.map((member, index) => {
        const physicalKey = scopedProjectKey(scopeProjectRef(member.environmentId, member.id));
        const label = labels[index] ?? {
          primary: member.name,
          namespace: null,
        };
        return {
          environmentId: member.environmentId,
          projectId: member.id,
          projectRef: scopeProjectRef(member.environmentId, member.id),
          cwd: member.cwd,
          label: formatWorktreeLabel(label),
          primaryLabel: label.primary,
          namespaceLabel: label.namespace,
          environmentRole: rolesByEnvironmentId.get(member.environmentId) ?? "local",
          // The worktree key is the physical project key — workers
          // automatically inherit because they share `projectId` with
          // their parent.
          worktreeKey: physicalKey,
        };
      });

      const activeWorktreeKey =
        routedPhysicalProjectKey !== null &&
        worktreeMembers.some((member) => member.worktreeKey === routedPhysicalProjectKey)
          ? routedPhysicalProjectKey
          : null;
      const representativeWorktreeKey = scopedProjectKey(
        scopeProjectRef(representative.environmentId, representative.id),
      );
      const targetWorktreeKey = activeWorktreeKey ?? representativeWorktreeKey;
      const targetEnvironmentRole =
        worktreeMembers.find((member) => member.worktreeKey === targetWorktreeKey)
          ?.environmentRole ??
        rolesByEnvironmentId.get(representative.environmentId) ??
        "local";

      const aggregatedAvailability = aggregateProjectAvailability(members);

      const snapshot: SidebarProjectSnapshot = {
        id: representative.id,
        environmentId: representative.environmentId,
        name: representative.name,
        cwd: representative.cwd,
        repositoryIdentity: representative.repositoryIdentity ?? null,
        availability: aggregatedAvailability,
        defaultModelSelection: representative.defaultModelSelection,
        defaultThreadEnvMode: representative.defaultThreadEnvMode ?? null,
        createdAt: representative.createdAt,
        updatedAt: representative.updatedAt,
        scripts: representative.scripts,
        projectKey: logicalKey,
        environmentPresence,
        source: deriveProjectSourceMetadata({
          runtime,
          environmentPresence,
          targetEnvironmentRole,
          workspaceIdentity,
        }),
        memberProjectRefs: refs,
        remoteEnvironmentLabels: remoteLabels,
        worktreeMembers,
        activeWorktreeKey,
      };
      result.push(snapshot);
    }
    return result;
  }, [
    orderedProjects,
    primaryEnvironmentId,
    resolveLogicalKey,
    routedPhysicalProjectKey,
    savedEnvironmentRegistry,
    savedEnvironmentRuntimeById,
    workspaceIdentityByPhysicalProjectKey,
  ]);

  const sidebarProjectByKey = useMemo(
    () => new Map(sidebarProjects.map((project) => [project.projectKey, project] as const)),
    [sidebarProjects],
  );

  const sidebarThreadByKey = useMemo(
    () =>
      new Map(
        sidebarThreads.map(
          (thread) =>
            [scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)), thread] as const,
        ),
      ),
    [sidebarThreads],
  );

  // Single answer to "which project is the route in?". Both the sidebar's logical
  // project key and the `/new` composer's project target derive from this one
  // lookup, so they cannot drift apart (CMMD-2266).
  const activeRouteProjectRef = useMemo(() => {
    if (!routeThreadKey) return null;
    const activeThread = sidebarThreadByKey.get(routeThreadKey);
    if (!activeThread) return null;
    return {
      environmentId: activeThread.environmentId,
      projectId: activeThread.projectId,
    };
  }, [routeThreadKey, sidebarThreadByKey]);

  const activeRouteProjectKey = useMemo(() => {
    if (!activeRouteProjectRef) return null;
    const physicalKey = scopedProjectKey(
      scopeProjectRef(activeRouteProjectRef.environmentId, activeRouteProjectRef.projectId),
    );
    return physicalToLogicalKey.get(physicalKey) ?? physicalKey;
  }, [activeRouteProjectRef, physicalToLogicalKey]);

  const threadsByProjectKey = useMemo(() => {
    const next = new Map<string, SidebarThreadSummary[]>();
    for (const thread of sidebarThreads) {
      const physicalKey = scopedProjectKey(scopeProjectRef(thread.environmentId, thread.projectId));
      const logicalKey = physicalToLogicalKey.get(physicalKey) ?? physicalKey;
      const existing = next.get(logicalKey);
      if (existing) {
        existing.push(thread);
      } else {
        next.set(logicalKey, [thread]);
      }
    }
    return next;
  }, [sidebarThreads, physicalToLogicalKey]);

  const visibleThreads = useMemo(
    () => sidebarThreads.filter((thread) => thread.archivedAt === null),
    [sidebarThreads],
  );

  const sortedProjects = useMemo(() => {
    const sortableProjects = sidebarProjects.map((project) => ({
      ...project,
      id: project.projectKey,
    }));
    const sortableThreads = visibleThreads.map((thread) => {
      const physicalKey = scopedProjectKey(scopeProjectRef(thread.environmentId, thread.projectId));
      return {
        ...thread,
        projectId: (physicalToLogicalKey.get(physicalKey) ??
          physicalKey) as typeof thread.projectId,
      };
    });
    return sortProjectsForSidebar(
      sortableProjects,
      sortableThreads,
      sidebarProjectSortOrder,
    ).flatMap((project) => {
      const resolvedProject = sidebarProjectByKey.get(project.id);
      return resolvedProject ? [resolvedProject] : [];
    });
  }, [
    sidebarProjectSortOrder,
    physicalToLogicalKey,
    sidebarProjectByKey,
    sidebarProjects,
    visibleThreads,
  ]);

  const isManualProjectSorting = sidebarProjectSortOrder === "manual";

  const visibleSidebarThreadKeys = useMemo(
    () =>
      sortedProjects.flatMap((project) => {
        const projectThreads = sortThreads(
          (threadsByProjectKey.get(project.projectKey) ?? []).filter(
            (thread) => thread.archivedAt === null,
          ),
          sidebarThreadSortOrder,
        );
        const projectExpanded = projectExpandedById[project.projectKey] ?? true;
        if (!projectExpanded) return [];
        const isThreadListExpanded = expandedThreadListsByProject.has(project.projectKey);
        const hasOverflowingThreads = projectThreads.length > THREAD_PREVIEW_LIMIT;
        const previewThreads =
          isThreadListExpanded || !hasOverflowingThreads
            ? projectThreads
            : projectThreads.slice(0, THREAD_PREVIEW_LIMIT);
        return previewThreads.map((thread) =>
          scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
        );
      }),
    [
      sidebarThreadSortOrder,
      expandedThreadListsByProject,
      projectExpandedById,
      sortedProjects,
      threadsByProjectKey,
    ],
  );

  const threadJumpCommandByKey = useMemo(() => {
    const mapping = new Map<string, NonNullable<ReturnType<typeof threadJumpCommandForIndex>>>();
    for (const [visibleThreadIndex, threadKey] of visibleSidebarThreadKeys.entries()) {
      const jumpCommand = threadJumpCommandForIndex(visibleThreadIndex);
      if (!jumpCommand) return mapping;
      mapping.set(threadKey, jumpCommand);
    }
    return mapping;
  }, [visibleSidebarThreadKeys]);

  const threadJumpThreadKeys = useMemo(
    () => [...threadJumpCommandByKey.keys()],
    [threadJumpCommandByKey],
  );

  const prewarmedSidebarThreadKeys = useMemo(
    () => getSidebarThreadIdsToPrewarm(visibleSidebarThreadKeys),
    [visibleSidebarThreadKeys],
  );
  const prewarmedSidebarThreadRefs = useMemo(
    () =>
      prewarmedSidebarThreadKeys.flatMap((threadKey) => {
        const ref = parseScopedThreadKey(threadKey);
        return ref ? [ref] : [];
      }),
    [prewarmedSidebarThreadKeys],
  );

  useEffect(() => {
    const releases = prewarmedSidebarThreadRefs.map((ref) =>
      retainThreadDetailSubscription(ref.environmentId, ref.threadId),
    );
    return () => {
      for (const release of releases) release();
    };
  }, [prewarmedSidebarThreadRefs]);

  return {
    physicalToLogicalKey,
    sidebarProjects,
    sidebarProjectByKey,
    sidebarThreadByKey,
    threadsByProjectKey,
    activeRouteProjectKey,
    activeRouteProjectRef,
    sortedProjects,
    isManualProjectSorting,
    visibleSidebarThreadKeys,
    threadJumpCommandByKey,
    threadJumpThreadKeys,
  };
}
